import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Box, Alert, CircularProgress, Button
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { swarmApi } from '../api';

function Nodes() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await swarmApi.listNodes();
      setNodes(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch swarm nodes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  /** Case-insensitive check for Docker node state (may return "Ready" or "ready") */
  const isReady = (state) => typeof state === 'string' && state.toLowerCase() === 'ready';

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Swarm Nodes</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Hostname</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Availability</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Engine</TableCell>
              <TableCell>Labels</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.map((n) => (
              <TableRow key={n.id || n.iD} hover>
                <TableCell>
                  <Typography variant="body1" fontWeight="bold">{n.hostname}</Typography>
                  <Typography variant="caption" color="text.secondary">{n.id || n.iD}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={n.role} color={n.role === 'manager' ? 'primary' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={n.state || 'unknown'} color={isReady(n.state) ? 'success' : 'error'} size="small" />
                </TableCell>
                <TableCell>{n.availability}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{n.addr || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{n.engineVersion || '—'}</Typography>
                </TableCell>
                <TableCell>
                  {Object.entries(n.labels || {}).map(([key, val]) => (
                    <Chip key={key} label={`${key}=${val}`} size="small" variant="outlined" sx={{ m: 0.5 }} />
                  ))}
                  {Object.keys(n.labels || {}).length === 0 && (
                    <Typography variant="body2" color="text.secondary">No labels natively applied</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {nodes.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">No nodes found in swarm</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}

export default Nodes;
